// src/pages/superadmin/LoginLogs.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Table, Badge, Button, Form,
  InputGroup, Modal, Spinner, Pagination,
} from 'react-bootstrap';
import {
  ShieldCheck, XCircle, Search, Filter, Eye, RefreshCw,
  Globe, Monitor, Clock, User, Building2, MapPin, Wifi,
  AlertTriangle, CheckCircle, LogIn,
} from 'lucide-react';
import axiosInstance from '../../api/axiosConfig';

// ─── helpers ─────────────────────────────────────────────────────────────────
const fmt = (dt) =>
  dt
    ? new Date(dt).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
      })
    : '—';

const FAILURE_LABELS = {
  user_not_found:         'Email not found',
  invalid_password:       'Wrong password',
  account_inactive:       'Account inactive',
  subscription_suspended: 'Subscription suspended',
  unknown:                'Unknown error',
};

function StatusBadge({ status }) {
  return status === 'success' ? (
    <Badge bg="success" className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
      <CheckCircle size={12} /> Success
    </Badge>
  ) : (
    <Badge bg="danger" className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
      <XCircle size={12} /> Failed
    </Badge>
  );
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────
function DetailModal({ log, onClose }) {
  if (!log) return null;

  const DetailRow = ({ icon: Icon, label, value, mono = false }) => (
    <div className="d-flex align-items-start gap-3 py-2 border-bottom">
      <div
        className="d-flex align-items-center justify-content-center rounded flex-shrink-0"
        style={{ width: 36, height: 36, background: '#f0f4ff' }}
      >
        <Icon size={16} color="#4f46e5" />
      </div>
      <div className="flex-grow-1">
        <div className="text-muted" style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </div>
        <div style={{ fontSize: '0.95rem', fontWeight: 500, fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-all' }}>
          {value || <span className="text-muted">—</span>}
        </div>
      </div>
    </div>
  );

  return (
    <Modal show onHide={onClose} size="md" centered>
      <Modal.Header closeButton className="border-bottom-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: '1.1rem' }}>
          <LogIn size={20} color="#4f46e5" />
          Login Log Detail
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">

        {/* Status banner */}
        <div
          className="rounded-3 p-3 mb-3 d-flex align-items-center gap-2"
          style={{
            background: log.status === 'success' ? '#d1fae5' : '#fee2e2',
            border: `1px solid ${log.status === 'success' ? '#6ee7b7' : '#fca5a5'}`,
          }}
        >
          {log.status === 'success'
            ? <><ShieldCheck size={20} color="#059669" /><strong style={{ color: '#065f46' }}>Login Successful</strong></>
            : <><AlertTriangle size={20} color="#dc2626" /><strong style={{ color: '#7f1d1d' }}>Login Failed — {FAILURE_LABELS[log.failure_reason] || log.failure_reason || 'Unknown reason'}</strong></>
          }
        </div>

        {/* Identity */}
        <div className="mb-1 text-muted" style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          Identity
        </div>
        <DetailRow icon={User}     label="Full Name"    value={log.full_name || 'Not found'} />
        <DetailRow icon={LogIn}    label="Email"        value={log.email}     mono />
        <DetailRow icon={ShieldCheck} label="Role"      value={log.role ? log.role.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null} />
        <DetailRow icon={Building2} label="Company"     value={log.company_name} />

        {/* Network */}
        <div className="mt-3 mb-1 text-muted" style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          Network
        </div>
        <DetailRow icon={Wifi}    label="IP Address"   value={log.ip_address}  mono />
        <DetailRow icon={MapPin}  label="Location"     value={log.location} />
        <DetailRow icon={Monitor} label="Browser"      value={log.browser} />

        {/* User Agent (collapsible) */}
        {log.user_agent && (
          <details className="mt-2">
            <summary className="text-muted" style={{ fontSize: '0.8rem', cursor: 'pointer' }}>
              Raw User-Agent string
            </summary>
            <div style={{ fontSize: '0.75rem', fontFamily: 'monospace', background: '#f8f9fa', borderRadius: 6, padding: '8px 10px', marginTop: 6, wordBreak: 'break-all', color: '#6b7280' }}>
              {log.user_agent}
            </div>
          </details>
        )}

        {/* Timestamp */}
        <div className="mt-3 mb-1 text-muted" style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          When
        </div>
        <DetailRow icon={Clock} label="Date & Time (IST)" value={fmt(log.created_at)} />
        <div className="mt-1 text-muted" style={{ fontSize: '0.78rem' }}>
          Log ID: #{log.id}
        </div>
      </Modal.Body>
      <Modal.Footer className="border-top-0">
        <Button variant="outline-secondary" size="sm" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function LoginLogs() {
  const [logs,         setLogs]         = useState([]);
  const [companies,    setCompanies]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [total,        setTotal]        = useState(0);
  const [totalPages,   setTotalPages]   = useState(1);
  const [selectedLog,  setSelectedLog]  = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filters
  const [page,       setPage]       = useState(1);
  const [status,     setStatus]     = useState('');
  const [companyId,  setCompanyId]  = useState('');
  const [search,     setSearch]     = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom,   setDateFrom]   = useState('');
  const [dateTo,     setDateTo]     = useState('');

  const LIMIT = 20;

  // ── fetch companies for dropdown ─────────────────────────────────────────
  useEffect(() => {
    axiosInstance.get('/superadmin/login-logs-companies')
      .then(r => setCompanies(r.data.companies || []))
      .catch(() => {});
  }, []);

  // ── fetch logs ────────────────────────────────────────────────────────────
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (status)    params.status     = status;
      if (companyId) params.company_id = companyId;
      if (search)    params.search     = search;
      if (dateFrom)  params.date_from  = dateFrom;
      if (dateTo)    params.date_to    = dateTo;

      const { data } = await axiosInstance.get('/superadmin/login-logs', { params });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch login logs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, status, companyId, search, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // ── view detail ───────────────────────────────────────────────────────────
  const viewDetail = async (id) => {
    setDetailLoading(true);
    try {
      const { data } = await axiosInstance.get(`/superadmin/login-logs/${id}`);
      setSelectedLog(data);
    } catch (err) {
      console.error('Failed to fetch log detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  // ── filter helpers ────────────────────────────────────────────────────────
  const applySearch = () => { setSearch(searchInput); setPage(1); };
  const resetFilters = () => {
    setStatus(''); setCompanyId(''); setSearch(''); setSearchInput('');
    setDateFrom(''); setDateTo(''); setPage(1);
  };
  const handleSearchKey = (e) => { if (e.key === 'Enter') applySearch(); };

  // ── stats cards ──────────────────────────────────────────────────────────
  const successCount = logs.filter(l => l.status === 'success').length;
  const failedCount  = logs.filter(l => l.status === 'failed').length;

  // ── pagination items ─────────────────────────────────────────────────────
  const paginationItems = () => {
    const items = [];
    const delta = 2;
    const left  = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);

    items.push(<Pagination.Item key={1} active={page === 1} onClick={() => setPage(1)}>1</Pagination.Item>);
    if (left > 2) items.push(<Pagination.Ellipsis key="l-ellipsis" disabled />);
    for (let p = left; p <= right; p++) {
      items.push(<Pagination.Item key={p} active={page === p} onClick={() => setPage(p)}>{p}</Pagination.Item>);
    }
    if (right < totalPages - 1) items.push(<Pagination.Ellipsis key="r-ellipsis" disabled />);
    if (totalPages > 1) {
      items.push(<Pagination.Item key={totalPages} active={page === totalPages} onClick={() => setPage(totalPages)}>{totalPages}</Pagination.Item>);
    }
    return items;
  };

  return (
    <Container fluid className="py-4">

      {/* ── Header ── */}
      <Row className="mb-4 align-items-center">
        <Col>
          <h4 className="mb-1 fw-bold d-flex align-items-center gap-2">
            <LogIn size={22} color="#4f46e5" />
            Login Logs
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Track every login attempt across all tenant companies — who, where, and when.
          </p>
        </Col>
        <Col xs="auto">
          <Button variant="outline-secondary" size="sm" onClick={fetchLogs} disabled={loading}
            className="d-flex align-items-center gap-1">
            <RefreshCw size={14} className={loading ? 'spin-icon' : ''} />
            Refresh
          </Button>
        </Col>
      </Row>

      {/* ── Summary stats ── */}
      <Row className="g-3 mb-4">
        <Col xs={12} sm={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <div className="rounded-3 p-2" style={{ background: '#eff6ff' }}>
                <LogIn size={22} color="#2563eb" />
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.78rem', fontWeight: 600 }}>TOTAL (THIS PAGE)</div>
                <div className="fw-bold fs-5">{total.toLocaleString()}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <div className="rounded-3 p-2" style={{ background: '#f0fdf4' }}>
                <CheckCircle size={22} color="#16a34a" />
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.78rem', fontWeight: 600 }}>SUCCESSFUL (THIS PAGE)</div>
                <div className="fw-bold fs-5 text-success">{successCount}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <div className="rounded-3 p-2" style={{ background: '#fef2f2' }}>
                <XCircle size={22} color="#dc2626" />
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.78rem', fontWeight: 600 }}>FAILED (THIS PAGE)</div>
                <div className="fw-bold fs-5 text-danger">{failedCount}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Filters ── */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row className="g-2 align-items-end">
            {/* Search */}
            <Col xs={12} md={4}>
              <Form.Label className="text-muted small fw-semibold mb-1">Search</Form.Label>
              <InputGroup size="sm">
                <InputGroup.Text><Search size={14} /></InputGroup.Text>
                <Form.Control
                  placeholder="Email or name..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyDown={handleSearchKey}
                />
                <Button variant="outline-primary" onClick={applySearch}>Go</Button>
              </InputGroup>
            </Col>

            {/* Status */}
            <Col xs={6} md={2}>
              <Form.Label className="text-muted small fw-semibold mb-1">Status</Form.Label>
              <Form.Select size="sm" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
                <option value="">All</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </Form.Select>
            </Col>

            {/* Company */}
            <Col xs={6} md={2}>
              <Form.Label className="text-muted small fw-semibold mb-1">Company</Form.Label>
              <Form.Select size="sm" value={companyId} onChange={e => { setCompanyId(e.target.value); setPage(1); }}>
                <option value="">All Companies</option>
                {companies.map(c => (
                  <option key={c.company_id} value={c.company_id}>{c.company_name}</option>
                ))}
              </Form.Select>
            </Col>

            {/* Date from */}
            <Col xs={6} md={2}>
              <Form.Label className="text-muted small fw-semibold mb-1">From</Form.Label>
              <Form.Control size="sm" type="date" value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setPage(1); }} />
            </Col>

            {/* Date to */}
            <Col xs={6} md={2}>
              <Form.Label className="text-muted small fw-semibold mb-1">To</Form.Label>
              <Form.Control size="sm" type="date" value={dateTo}
                onChange={e => { setDateTo(e.target.value); setPage(1); }} />
            </Col>

            {/* Reset */}
            <Col xs="auto">
              <Button variant="outline-secondary" size="sm" onClick={resetFilters}
                className="d-flex align-items-center gap-1" title="Reset filters">
                <Filter size={13} /> Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Table ── */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="text-muted">Loading login logs...</span>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <LogIn size={36} className="mb-2 opacity-25" />
              <div>No login logs found for the selected filters.</div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle" style={{ fontSize: '0.88rem' }}>
                <thead style={{ background: '#f8fafc' }}>
                  <tr>
                    <th className="ps-4 py-3 border-0 text-muted fw-semibold">#</th>
                    <th className="py-3 border-0 text-muted fw-semibold">User</th>
                    <th className="py-3 border-0 text-muted fw-semibold">Company</th>
                    <th className="py-3 border-0 text-muted fw-semibold">IP Address</th>
                    <th className="py-3 border-0 text-muted fw-semibold">Location</th>
                    <th className="py-3 border-0 text-muted fw-semibold">Browser</th>
                    <th className="py-3 border-0 text-muted fw-semibold">Status</th>
                    <th className="py-3 border-0 text-muted fw-semibold">Date & Time (IST)</th>
                    <th className="pe-4 py-3 border-0 text-muted fw-semibold text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td className="ps-4 text-muted" style={{ fontSize: '0.78rem' }}>
                        {(page - 1) * LIMIT + idx + 1}
                      </td>
                      <td>
                        <div className="fw-semibold">{log.full_name || <span className="text-muted fst-italic">Unknown</span>}</div>
                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>{log.email}</div>
                        {log.role && (
                          <div style={{ fontSize: '0.72rem' }}>
                            <Badge bg="light" text="secondary" className="mt-1">
                              {log.role.replace(/-/g, ' ')}
                            </Badge>
                          </div>
                        )}
                      </td>
                      <td>
                        {log.company_name
                          ? <span className="d-flex align-items-center gap-1"><Building2 size={13} className="text-muted" />{log.company_name}</span>
                          : <span className="text-muted fst-italic">—</span>
                        }
                      </td>
                      <td>
                        <code style={{ fontSize: '0.8rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>
                          {log.ip_address || '—'}
                        </code>
                      </td>
                      <td>
                        <span className="d-flex align-items-center gap-1">
                          <Globe size={13} className="text-muted flex-shrink-0" />
                          <span className="text-truncate" style={{ maxWidth: 140 }}>{log.location || '—'}</span>
                        </span>
                      </td>
                      <td>
                        <span className="d-flex align-items-center gap-1">
                          <Monitor size={13} className="text-muted" />
                          {log.browser || '—'}
                        </span>
                      </td>
                      <td>
                        <StatusBadge status={log.status} />
                        {log.failure_reason && (
                          <div className="text-danger mt-1" style={{ fontSize: '0.72rem' }}>
                            {FAILURE_LABELS[log.failure_reason] || log.failure_reason}
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontSize: '0.82rem' }}>
                          {new Date(log.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {new Date(log.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })} IST
                        </div>
                      </td>
                      <td className="pe-4 text-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="d-flex align-items-center gap-1 mx-auto"
                          onClick={() => viewDetail(log.id)}
                          disabled={detailLoading}
                        >
                          <Eye size={13} />
                          Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>

        {/* ── Pagination ── */}
        {totalPages > 1 && !loading && (
          <Card.Footer className="bg-white border-top-0 d-flex align-items-center justify-content-between px-4 py-2">
            <span className="text-muted" style={{ fontSize: '0.82rem' }}>
              Showing {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} of {total.toLocaleString()} entries
            </span>
            <Pagination size="sm" className="mb-0">
              <Pagination.Prev disabled={page === 1}        onClick={() => setPage(p => p - 1)} />
              {paginationItems()}
              <Pagination.Next disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
            </Pagination>
          </Card.Footer>
        )}
      </Card>

      {/* ── Detail Modal ── */}
      {selectedLog && <DetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />}

      {/* Spinner overlay while loading detail */}
      {detailLoading && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spinner animation="border" variant="light" />
        </div>
      )}

      {/* Refresh spin animation */}
      <style>{`.spin-icon { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </Container>
  );
}
